<script setup>
import { ref, onMounted, computed, nextTick } from "vue";
import { useScrollStore } from "@/stores/storeScroll";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { useQuizMatchesStore } from "@/stores/storeQuizMatches";
import { useModals } from "@/composables/useModals";
import { useGameHistory } from "@/composables/useGameHistory";
import { sendMetrik, trackPixel } from "@/utils/metrika";
import QuizEndGameMatches from "@/components/quize/QuizEndGameMatches.vue";
import QuizWelcomeScreen from "@/components/quize/QuizWelcomeScreen.vue";
import QuizSlideTimer from "@/components/quize/QuizSlideTimer.vue";
import QuizMatch from "@/components/quize/QuizMatch.vue";
import BaseLoader from "@/components/blocks/BaseLoader.vue";
import BaseButtonUploadReceipt from "@/components/ui/BaseButtonUploadReceipt.vue";

const storeScroll = useScrollStore();
const storeSettings = useSettingsStore();
const storeRequests = useRequestsStore();
const storeQuizMatches = useQuizMatchesStore();
const { openModal } = useModals();
const { remainingTime, initTimer } = useGameHistory();

const gameTextError = computed(() => storeSettings.statusStockActive ? "Игра доступна только новым участникам до подтверждения первого чека." : `Игра доступна только в период проведения акции: с ${formatDate(storeSettings.startDate)} по ${formatDate(storeSettings.endDate)}.`)

const isTextInfo = ref(null);
let timer = null;

const showInfo = () => {
	isTextInfo.value = true;
	clearTimeout(timer);
	timer = setTimeout(() => {
		isTextInfo.value = null;
	}, 3000);
};

const hideInfo = () => {
	isTextInfo.value = null;
	clearTimeout(timer);
};

const text = computed(() => {
	return storeRequests.isAuthenticated
		? "Подарок откроется после одобрения Вашего первого чека."
		: "Вы получите подарок. Чтобы открыть — зарегистрируйтесь и загрузите чек и дождитесь его одобрения.";
})

onMounted(async () => {
	await initTimer();
	trackPixel('VIEW', 'games')
});
</script>
<template>
	<section class="quiz" :style="{ paddingTop: storeScroll.paddingTop }">
		<BaseLoader v-if="storeQuizMatches.isLoaderQuiz" />
		<Transition name="slide-up" mode="out-in">
			<QuizWelcomeScreen v-if="storeQuizMatches.isShowWelcomScreen" class="--matches">
				<template #nameGame>Игра «3 в ряд»</template>
				<template #descGame>Соберите как можно больше комбинаций за 30 секунд</template>
				<template #btnStartGame v-if="storeQuizMatches.isShowErrorBanner">					
					<div class="quiz-matches">
						<div class="quiz-matches__desc">
							<p style="color:#FF3E3E">Упс, что-то пошло не так.</p>
						</div>
						<button @click="storeQuizMatches.startGame()" class="btn">Перезапустить</button>
					</div>
				</template>
				<template #btnStartGame v-else>
					<button v-if="storeQuizMatches.isMatchGameAvailable" @click="storeQuizMatches.startGame()" class="btn">Начать игру</button>
					<button v-else class="game-btn-disabled" @mouseenter="showInfo()" @click="showInfo()">
						игра недоступна
						<svg width="20" height="20"><use href="#question"></use></svg>
						<Transition>
							<span class="game-info-text" v-if="isTextInfo">
								<svg width="20" height="20" @click.stop="hideInfo"><use href="#x"></use></svg>
								{{ gameTextError }}
							</span>
						</Transition>
					</button>
					<div class="quiz-matches" v-if="storeQuizMatches.isShowTimer">
						<QuizSlideTimer :time="remainingTime" />
						<div class="quiz-matches__desc">
							<p>{{ text }}</p>
							<p>Новое прохождение заменит текущие результаты.</p>
						</div>
						<BaseButtonUploadReceipt v-if="storeRequests.isAuthenticated" class="quiz-matches__btn">Загрузить чек</BaseButtonUploadReceipt>
						<button v-else @click="openModal('registration')" class="btn">Зарегистрироваться</button>
					</div>
				</template>
				<template #imgGame>
					<picture class="">
						<source media="(max-width: 480px)" srcset="@/assets/img/games/start_match_mobile.webp" />
						<img class="full-img" width="549" height="549" src="@/assets/img/games/start_match.webp" alt="изображение" />
					</picture>
				</template>
			</QuizWelcomeScreen>

			<QuizMatch v-else-if="storeQuizMatches.isShowGame" />

			<QuizEndGameMatches v-else-if="storeQuizMatches.isShowEndGame" />
		</Transition>
	</section>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
.quiz {
	background-image: url("@/assets/img/games/welcome_game_bg.webp");
	background-repeat: no-repeat;
	background-position: center; /* x y*/
	background-size: cover; /* w h*/
	@media (max-width: vars.$xx) {
		background-image: none;
	}
	.game-btn-disabled {
		width: fit-content;
	}
}
.quiz-matches {
	display: grid;
	gap: 4rem;
	max-width: 62.2rem;
	@media (max-width: vars.$xx) {
		justify-items: center;
		gap: 2.4rem;
	}
	&__desc {
		@media (max-width: vars.$x480) {
			text-align: center;
		}
		p {
			&:not(:last-child) {
				margin-bottom: 20px;
			}
		}
	}
}
.quiz-padding {
	padding: 4.3rem 0 7.4rem;
	@media (max-width: vars.$x480) {
		padding: 2.7rem 0 7.4rem;
	}
}

.slide-up-enter-active,
.slide-up-leave-active {
	transition: all 0.25s ease-out;
}

.slide-up-enter-from {
	opacity: 0;
	transform: translateY(30px);
}

.slide-up-leave-to {
	opacity: 0;
	transform: translateY(-30px);
}
</style>
